/**
 * notificationPreferences.ts — Notification preferences localStorage helpers
 *
 * Key: litpulse_notification_prefs_v1_{userId}
 *
 * These preferences are PRESENTATION-ONLY in Cycle 6A.
 * They are NOT enforced by the scheduler, SendGrid, or any backend service.
 * Future cycles will read this data to control live delivery.
 */

export type DeliveryChannel = 'email' | 'email_and_inapp';

export const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export type DayOfWeek = typeof DAYS_OF_WEEK[number];

export interface QuietHours {
  enabled: boolean;
  startHour: number;   // 1-12
  startAmpm: 'AM' | 'PM';
  endHour: number;     // 1-12
  endAmpm: 'AM' | 'PM';
  days: DayOfWeek[];
}

export interface NotificationPrefs {
  quietHours: QuietHours;
  delivery: DeliveryChannel;
}

const DEFAULT_PREFS: NotificationPrefs = {
  quietHours: {
    enabled: false,
    startHour: 10,
    startAmpm: 'PM',
    endHour: 7,
    endAmpm: 'AM',
    days: [...DAYS_OF_WEEK],
  },
  delivery: 'email',
};

function storageKey(userId: string): string {
  return `litpulse_notification_prefs_v1_${userId}`;
}

export function getNotificationPrefs(userId: string): NotificationPrefs {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.quietHours || !parsed.delivery) {
      localStorage.removeItem(storageKey(userId));
      return { ...DEFAULT_PREFS };
    }
    return parsed;
  } catch {
    localStorage.removeItem(storageKey(userId));
    return { ...DEFAULT_PREFS };
  }
}

export function saveNotificationPrefs(userId: string, prefs: NotificationPrefs): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch { /* fail silently */ }
}
